<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Drawer, DrawerContent, DrawerItem } from '@progress/kendo-vue-layout'
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { setStoreLogger } from '@/logger.ts'
import AoeCartDetail from './AoeCartDetail.vue'
import {
  GetCartItemCount,
  GetCurrentCartIdentifiers,
  CartLocalStorageKey,
} from '@/services/CartService'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('AOECartDrawer.vue')

const expanded = ref(false)
const cartQuantity = ref(0)
const drawerItems = ref([
  {
    text: t('cart.ViewCart'),
    selected: false,
    data: { path: '/' },
  },
])

function cartDrawerClick() {
  logger.debug('cartDrawerClick')
  expanded.value = !expanded.value
}

async function storageHandler(event: StorageEvent) {
  logger.debug('AOECartDrawer storageHandler - event.key:', event.key)

  if (event.key !== CartLocalStorageKey) {
    return
  }

  logger.debug('AOECartDrawer - CART EVENT DETECTED, updating count...')
  const cartIdentifiers = GetCurrentCartIdentifiers()

  if (cartIdentifiers) {
    cartQuantity.value = await GetCartItemCount(cartIdentifiers.aoeCartIdToken)
    logger.debug('AOECartDrawer - updated cartQuantity to:', cartQuantity.value)
  } else {
    cartQuantity.value = 0
  }
}

onMounted(async () => {
  const cartIdentifiers = GetCurrentCartIdentifiers()
  if (cartIdentifiers) {
    cartQuantity.value = await GetCartItemCount(cartIdentifiers.aoeCartIdToken)
  } else {
    cartQuantity.value = 0
  }

  window.addEventListener('storage', storageHandler, false)
})

onBeforeUnmount(() => {
  window.removeEventListener('storage', storageHandler, false)
})
</script>

<template>
  <div class="aoe-cart-drawer">
    <Drawer
      :expanded="expanded"
      :position="'end'"
      :mode="'overlay'"
      @overlayclick="cartDrawerClick"
      :items="
        drawerItems.map((item, index) => ({
          ...item,
        }))
      "
      :item="'CustomItem'"
    >
      <template v-slot:CustomItem="{ props }">
        <DrawerItem v-bind="props" @click="props.onClick">
          <AoeCartDetail :ispopover="true" @close-popover="cartDrawerClick"></AoeCartDetail>
        </DrawerItem>
      </template>
      <DrawerContent>
        <div class="k-form">
          <div class="k-form-field cart-drawer-button-container">
            <!-- TEAU-1059: Cart drawer button hidden via CSS (see styles below) -->
            <KButton class="btnViewCartDrawer" @click="cartDrawerClick">
              {{ t('cart.ViewCart') }}
            </KButton>
            <div v-if="cartQuantity > 0" class="cart-drawer-badge">
              {{ cartQuantity }}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  </div>
</template>

<style lang="scss">
/* Cart drawer overlay - floats above content without affecting layout */
/* !important required: Kendo theme CSS is not layered and would override these critical layout properties */
.aoe-cart-drawer .k-drawer-container {
  position: relative;
  width: 0 !important;
  height: 0 !important;
  overflow: visible !important;
  pointer-events: none;
}

.aoe-cart-drawer .k-drawer-container > * {
  pointer-events: auto;
}

/* !important required: Override Kendo's default drawer positioning to create fixed overlay */
.aoe-cart-drawer .k-drawer-wrapper {
  width: 600px !important;
  position: fixed !important;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1000;
  display: none;
  overflow: hidden;
}

/* !important required: Override Kendo's display logic for expanded state */
.aoe-cart-drawer .k-drawer-container.k-drawer-expanded .k-drawer-wrapper {
  display: block !important;
}

/* Ensure drawer content fits within 600px width */
/* !important required: Override Kendo's dynamic width calculations */
.aoe-cart-drawer .k-drawer-wrapper .k-drawer-content,
.aoe-cart-drawer .k-drawer-wrapper .k-drawer-item {
  width: 600px !important;
  max-width: 600px !important;
  overflow: hidden;
}

/* Remove excessive padding in popover mode to prevent truncation */
.k-drawer-wrapper .allCartItems {
  padding-right: $spacing-2;
  padding-left: $spacing-2;
}

.k-drawer-wrapper .popOverSummaryDiv {
  padding: $spacing-3 $spacing-2;
}

.k-drawer-wrapper .popOverCrosssell {
  padding: $spacing-3 $spacing-2;
}

/* Ensure cart prices and totals fit within drawer width */
.k-drawer-wrapper .cart-item-price,
.k-drawer-wrapper .cart-total-value,
.k-drawer-wrapper .lblSubtotalValue {
  max-width: 100%;
  word-wrap: break-word;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Fix cart item layout for drawer */
.k-drawer-wrapper .cart-item .row {
  width: 100%;
  max-width: 100%;
}

.k-drawer-wrapper .product-name {
  max-width: calc(100% - 120px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aoe-cart-drawer .k-drawer-item:hover {
  background-color: white;
  cursor: auto;
}

/* !important required: Override Kendo's content sizing to allow auto dimensions */
.aoe-cart-drawer .k-drawer-content {
  position: absolute;
  right: 0;
  top: 0;
  width: auto !important;
  height: auto !important;
  pointer-events: auto;
}

/* !important required: Override Kendo button defaults for custom cart drawer styling */
.btnViewCartDrawer {
  @include typography-h4;
  @include font-weight-medium;
  background-color: var(--color-teal);
  font-style: normal;
  letter-spacing: 0.6px;
  color: white;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  padding: $spacing-4 $spacing-2 !important;
  border-radius: $button-radius 0 0 $button-radius !important;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15) !important;
  border: none !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  white-space: nowrap !important;
  margin: 0 !important;
}

/* !important required: Ensure hover state overrides Kendo's hover styles */
.btnViewCartDrawer:hover {
  background-color: var(--color-teal-dark) !important;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.25) !important;
}

.cart-drawer-button-container {
  position: relative;
  display: inline-block;
}

.cart-drawer-badge {
  position: absolute;
  top: 50%;
  left: -10px;
  transform: translateY(-50%);
  background-color: #e74c3c;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

/* Mobile specific - horizontal bottom bar */
/* !important required: Override desktop styles for mobile layout */
@media screen and (max-width: 575px) {
  .cart-drawer-button-container .btnViewCartDrawer {
    display: none !important; /* TEAU-1059: Hide on mobile too */
    writing-mode: horizontal-tb !important;
    text-orientation: mixed !important;
    padding: $spacing-2 $spacing-4 !important;
    border-radius: $button-radius $button-radius 0 0 !important;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15) !important;
  }

  .btnViewCartDrawer:hover {
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.25) !important;
  }

  .cart-drawer-badge {
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
  }
}

@media screen and (min-width: 769px) {
  .k-drawer-item .aoe-cart-detail .allCartItems {
    padding-right: 0px;
  }
  /* !important required: Override Kendo's flex-basis calculation */
  .aoe-cart-drawer .k-drawer {
    width: 600px;
    flex-basis: inherit !important;
  }
}

/* TEAU-1059: Temporarily hide cart drawer button from UI (code preserved for future use) */
.cart-drawer-button-container .btnViewCartDrawer {
  display: none !important;
}
</style>
